/**
 * Moderator Tickets Page
 * Interface for moderators to manage tickets (pool, claimed, assigned) plus, for admins, the
 * statistics dashboard that used to live at its own /tickets/statistics route.
 *
 * Every list reads GET api/tickets/query, which pages, filters (escalation included) and sorts on
 * the server and returns the total. The old pool/claimed/assigned endpoints returned a bare first
 * page of 20 with no count, so the queue looked like "20 of 20" however long it really was, and
 * the escalation filter only ever searched those 20.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Chip, ToggleButtonGroup, ToggleButton, TextField, MenuItem } from '@mui/material'
import { Refresh, ViewModule, ViewList, FactCheck, Gavel } from '@mui/icons-material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query'
import { queryTickets, type TicketQuerySort } from '@api/services/tickets.api'
import { TicketSummaryDto, TicketEscalationLevel, TicketEscalationLabels } from '@api/types/tickets'
import {
  TICKET_SORTS,
  parseEscalation,
  parseListTab,
  parseSort,
  ticketListQuery,
  type ModeratorListTab,
  type TicketListContext,
} from '../components/ticketListQuery'
import { TicketTable } from '../components/TicketTable'
import { TicketStatistics } from '../components/TicketStatistics'
import { PageShell } from '@components/common/PageShell'
import { ResponsiveTabs } from '@components/common/ResponsiveTabs'
import { useAuthStore } from '@store/authStore'

type ModeratorTab = ModeratorListTab | 'statistics'
type ViewMode = 'cards' | 'table'

const ESCALATION_FILTERS: TicketEscalationLevel[] = [TicketEscalationLevel.Mods, TicketEscalationLevel.SystemModerators]

const LIST_TABS: { key: ModeratorListTab; label: string }[] = [
  { key: 'pool', label: 'Ticket Pool' },
  { key: 'claimed', label: 'My Claimed' },
  { key: 'assigned', label: 'Assigned to Me' },
]

const PAGE_SIZE_KEY = 'ticketPageSize'

const errorText = (err: unknown): string | null =>
  err ? (err instanceof Error && err.message ? err.message : 'Failed to load tickets') : null

export default function ModeratorTicketsPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const isAdmin = !!user?.roles?.includes('Admin')

  // Every filter lives in the URL so a filtered queue is linkable, survives a refresh, and the
  // detail page's Back can return to exactly this list. The URL is the only copy of them.
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const activeTab: ModeratorTab = tabParam === 'statistics' && isAdmin ? 'statistics' : parseListTab(tabParam)
  // "Validation" filter (Backlog 057): only validation items (types 30-39).
  const validationOnly = searchParams.get('validation') === '1'
  // "Ownership claims" (type 40): the claims only site moderators decide. Exclusive with Validation.
  const claimsOnly = searchParams.get('claims') === '1'
  const escalationParam = searchParams.get('escalation')
  const escalationFilter = useMemo(() => parseEscalation(escalationParam), [escalationParam])
  const sort = parseSort(searchParams.get('sort'))

  /** One URL edit from a click; any filter change returns to the first page. */
  const editParams = (edit: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams)
    edit(next)
    next.delete('page')
    setSearchParams(next, { replace: true })
    setPage(0)
  }
  const toggleValidationOnly = () =>
    editParams((next) => {
      if (validationOnly) next.delete('validation')
      else {
        next.set('validation', '1')
        next.delete('claims')
      }
    })
  const toggleClaimsOnly = () =>
    editParams((next) => {
      if (claimsOnly) next.delete('claims')
      else {
        next.set('claims', '1')
        next.delete('validation')
      }
    })
  const toggleEscalation = (level: TicketEscalationLevel) =>
    editParams((next) => {
      const set = new Set(escalationFilter)
      if (set.has(level)) set.delete(level)
      else set.add(level)
      if (set.size === 0) next.delete('escalation')
      else next.set('escalation', [...set].sort().join(','))
    })
  const changeSort = (value: TicketQuerySort) =>
    editParams((next) => {
      if (value === 'priority') next.delete('sort')
      else next.set('sort', value)
    })

  // The page is read from the URL once (Back from a ticket brings ?page= along) and then kept in
  // state: the pager is not worth a history write per click.
  const [page, setPage] = useState(() => Math.max(0, Number(searchParams.get('page')) || 0))
  const [pageSize, setPageSize] = useState(() => {
    const saved = Number(localStorage.getItem(PAGE_SIZE_KEY))
    return [10, 25, 50, 100].includes(saved) ? saved : 25
  })
  const handlePageSizeChange = (size: number) => {
    setPageSize(size)
    localStorage.setItem(PAGE_SIZE_KEY, String(size))
  }

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('ticketViewMode')
    return (saved as ViewMode) || 'cards'
  })

  // Statistics is admin-only; a moderator whose stale URL points at ?tab=statistics falls back
  // to the pool.
  const listTab: ModeratorListTab = activeTab === 'statistics' ? 'pool' : activeTab
  const listQuery = useMemo(() => ticketListQuery(searchParams, listTab), [searchParams, listTab])
  const filterKey = JSON.stringify({ ...listQuery, sort: undefined })

  // A tab switch (written to the URL by ResponsiveTabs) starts at the first page too. Skipped on
  // the first render so a ?page= from Back survives.
  const firstTab = useRef(true)
  useEffect(() => {
    if (firstTab.current) {
      firstTab.current = false
      return
    }
    setPage(0)
  }, [listTab])

  const list = useQuery({
    queryKey: ['tickets', 'moderator-list', listQuery, page, pageSize],
    queryFn: ({ signal }) => queryTickets({ ...listQuery, skip: page * pageSize, take: pageSize }, signal),
    enabled: activeTab !== 'statistics',
    // Keep the old page on screen while the next one loads instead of flashing skeletons.
    placeholderData: keepPreviousData,
  })

  // Tab counts under the same filters: one row each, only the total is wanted.
  const counts = useQueries({
    queries: LIST_TABS.map((tab) => ({
      queryKey: ['tickets', 'moderator-count', tab.key, filterKey],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        queryTickets({ ...ticketListQuery(searchParams, tab.key), take: 1 }, signal).then((p) => p.totalCount),
      staleTime: 30_000,
    })),
  })

  const total = list.data?.totalCount
  // A page that emptied (tickets resolved under us) steps back to the last page that exists.
  useEffect(() => {
    if (total === undefined || page === 0) return
    const last = Math.max(0, Math.ceil(total / pageSize) - 1)
    if (page > last) setPage(last)
  }, [total, page, pageSize])

  const tabs = useMemo(() => {
    const listTabs = LIST_TABS.map((t, i) => ({ ...t, count: counts[i]?.data }))
    return isAdmin ? [...listTabs, { key: 'statistics', label: 'Statistics' }] : listTabs
  }, [isAdmin, counts])

  const refresh = () => {
    list.refetch()
    counts.forEach((c) => c.refetch())
  }

  const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode !== null) {
      setViewMode(newMode)
      localStorage.setItem('ticketViewMode', newMode)
    }
  }

  const rows = list.data?.items ?? []
  const handleTicketClick = (ticket: TicketSummaryDto) => {
    // The list's query (with the page it is on) and the ids it shows, so the ticket page can step
    // to the next one and return here.
    const search = new URLSearchParams(searchParams)
    if (page > 0) search.set('page', String(page))
    else search.delete('page')
    const ticketList: TicketListContext = {
      search: search.toString(),
      ids: rows.map((t) => t.id),
      skip: page * pageSize,
      total: total ?? rows.length,
    }
    navigate(`/tickets/${ticket.id}`, { state: { ticketList } })
  }

  const getEmptyMessage = () => {
    if (claimsOnly) return 'No ownership claims here'
    if (escalationFilter.length > 0) return 'No escalated tickets here'
    if (validationOnly) {
      switch (activeTab) {
        case 'pool':
          return 'No validation items in the pool'
        case 'claimed':
          return 'You have not claimed any validation items'
        case 'assigned':
          return 'You have no validation items assigned'
        default:
          return 'No validation items found'
      }
    }
    switch (activeTab) {
      case 'pool':
        return 'No tickets in the pool. Great work!'
      case 'claimed':
        return 'You have not claimed any tickets'
      case 'assigned':
        return 'You have no assigned tickets'
      default:
        return 'No tickets found'
    }
  }

  // The view toggle and Refresh only mean something on the list tabs: statistics fetches and
  // renders its own dashboard. On a phone both views are the same compact card, so no toggle.
  const actions =
    activeTab === 'statistics' ? null : (
      <>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
          aria-label="view mode"
          sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
        >
          <ToggleButton value="cards" aria-label="card view">
            <ViewModule />
          </ToggleButton>
          <ToggleButton value="table" aria-label="table view">
            <ViewList />
          </ToggleButton>
        </ToggleButtonGroup>

        <Button variant="outlined" startIcon={<Refresh />} onClick={refresh} disabled={list.isFetching}>
          Refresh
        </Button>
      </>
    )

  const toolbar = (
    <>
      <Chip
        icon={<FactCheck />}
        label="Validation"
        clickable
        onClick={toggleValidationOnly}
        color={validationOnly ? 'info' : 'default'}
        variant={validationOnly ? 'filled' : 'outlined'}
        aria-pressed={validationOnly}
      />
      <Chip
        icon={<Gavel />}
        label="Ownership claims"
        clickable
        onClick={toggleClaimsOnly}
        color={claimsOnly ? 'secondary' : 'default'}
        variant={claimsOnly ? 'filled' : 'outlined'}
        aria-pressed={claimsOnly}
      />
      {ESCALATION_FILTERS.map((level) => {
        const on = escalationFilter.includes(level)
        return (
          <Chip
            key={level}
            label={`Escalated: ${TicketEscalationLabels[level]}`}
            clickable
            onClick={() => toggleEscalation(level)}
            color={on ? (level === TicketEscalationLevel.SystemModerators ? 'error' : 'warning') : 'default'}
            variant={on ? 'filled' : 'outlined'}
            aria-pressed={on}
          />
        )
      })}
      <TextField
        select
        size="small"
        label="Sort"
        value={sort}
        onChange={(e) => changeSort(e.target.value as TicketQuerySort)}
        sx={{ width: { xs: '100%', sm: 180 }, ml: { sm: 'auto' } }}
      >
        {TICKET_SORTS.map((s) => (
          <MenuItem key={s.value} value={s.value}>
            {s.label}
          </MenuItem>
        ))}
      </TextField>
    </>
  )

  return (
    <PageShell title="Tickets" actions={actions}>
      <Box sx={{ mb: 2 }}>
        <ResponsiveTabs tabs={tabs} value={activeTab} onChange={() => undefined} />
      </Box>

      {activeTab === 'statistics' ? (
        <TicketStatistics />
      ) : (
        <TicketTable
          tickets={rows}
          total={total ?? 0}
          loading={list.isLoading}
          error={errorText(list.error)}
          onRetry={() => list.refetch()}
          emptyMessage={getEmptyMessage()}
          onTicketClick={handleTicketClick}
          showAssignee={activeTab === 'pool'}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          mode={viewMode === 'cards' ? 'cards' : 'auto'}
          toolbar={toolbar}
        />
      )}
    </PageShell>
  )
}
